import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import {
  BrowserRouter as Router,
  Link,
  Redirect,
  Route,
} from "react-router-dom";
import SelectClass from "../ChildComponent/SelectClass";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import AddClassFee from "./AddClassFee";
import MdlEditClassFee from "../dialog/MdlEditClassFee";

const MONTH_SHORT = {
  1: "Jan", 2: "Feb", 3: "Mar", 4: "Apr", 5: "May", 6: "Jun",
  7: "Jul", 8: "Aug", 9: "Sep", 10: "Oct", 11: "Nov", 12: "Dec",
};

const CARD_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%)`;

function formatINR(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function getFeeMonthRows(item) {
  if (Array.isArray(item?.months) && item.months.length > 0) {
    return item.months;
  }
  return String(item?.applicable_month || "")
    .split(",")
    .map((m) => m.trim())
    .filter(Boolean)
    .map((month) => ({
      month,
      fee_value: Number(item?.fee_value || 0),
    }));
}

function getReadmMonthRows(item) {
  if (Array.isArray(item?.readm_months) && item.readm_months.length > 0) {
    return item.readm_months;
  }
  return [];
}

function sumMonthValues(monthRows) {
  return monthRows.reduce((sum, row) => sum + Number(row.fee_value || 0), 0);
}

function parseMonthKey(monthKey) {
  const parts = String(monthKey || "").trim().split("-");
  if (parts.length < 2) {
    return { monthLabel: monthKey || "—", year: "", monthNum: "" };
  }
  const year = parts[0];
  const monthNum = String(parseInt(parts[1], 10)).padStart(2, "0");
  const month = parseInt(parts[1], 10);
  return {
    monthLabel: MONTH_SHORT[month] || monthNum,
    year,
    monthNum,
  };
}

function MonthFeeChip({ monthKey, feeValue, accentColor }) {
  const { monthLabel, year, monthNum } = parseMonthKey(monthKey);
  const isReadm = Boolean(accentColor);
  const valueBg = isReadm
    ? `linear-gradient(135deg, ${COLORS.green_light}, ${COLORS.green1})`
    : `linear-gradient(135deg, ${COLORS.green_light}, ${COLORS.green0})`;
  const valueColor = COLORS.leaf_green;
  const valueBorder = isReadm ? COLORS.card_green : COLORS.green_dark;

  return (
    <div
      title={monthKey}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        padding: "8px 6px",
        borderRadius: "10px",
        background: COLORS.white,
        border: `2px solid ${accentColor || COLORS.primary_color_shade3}`,
        boxShadow: "0 1px 4px rgba(2, 136, 209, 0.12)",
      }}
    >
      <span
        style={{
          fontSize: "15px",
          fontWeight: 800,
          color: COLORS.primary_color_shade9,
          lineHeight: 1.1,
          letterSpacing: "0.03em",
        }}
      >
        {monthLabel}
      </span>
      <span
        style={{
          fontSize: "11px",
          fontWeight: 600,
          color: COLORS.black7,
          marginTop: "3px",
          lineHeight: 1.2,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {year && monthNum ? `${year}-${monthNum}` : monthKey}
      </span>
      <span
        style={{
          fontSize: "12px",
          fontWeight: 800,
          color: valueColor,
          marginTop: "6px",
          lineHeight: 1.1,
          padding: "4px 8px",
          borderRadius: "6px",
          background: valueBg,
          border: `1px solid ${valueBorder}`,
          width: "100%",
          textAlign: "center",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatINR(feeValue)}
      </span>
    </div>
  );
}

const ClassFee = () => {
  const [loggedIn, setLoggedIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [SESSION_ID, setSESSION_ID] = useState("");
  const [CLASS_ID, setCLASS_ID] = useState("");
  const [SECTION_ID, setSECTION_ID] = useState("");
  const [mClassFee, setClassFee] = useState([]);
  const [SELECTED_CLASS_POSITION, setSELECTED_CLASS_POSITION] = useState("");
  const [editFeeItem, setEditFeeItem] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) setLoggedIn(false);
    window.scrollTo(0, 0);
  }, []);

  const sortedClassFee = useMemo(
    () =>
      [...mClassFee].sort((a, b) => {
        const totalA = sumMonthValues(getFeeMonthRows(a)) || Number(a.fee_value || 0);
        const totalB = sumMonthValues(getFeeMonthRows(b)) || Number(b.fee_value || 0);
        return totalB - totalA;
      }),
    [mClassFee]
  );

  const totalFeeValue = useMemo(
    () =>
      sortedClassFee.reduce((sum, fee) => {
        const monthRows = getFeeMonthRows(fee);
        if (monthRows.length > 0) {
          return sum + sumMonthValues(monthRows);
        }
        return sum + Number(fee.fee_value || 0);
      }, 0),
    [sortedClassFee]
  );

  const CallBackClassSelect = (
    class_id,
    section_id,
    session_id,
    selected_position
  ) => {
    setCLASS_ID(class_id);
    setSECTION_ID(section_id);
    setSESSION_ID(session_id);
    setSELECTED_CLASS_POSITION(selected_position);
    getClassFee(class_id, session_id);
  };

  const getClassFee = (classId, sessionId) => {
    setClassFee([]);
    setLoading(true);

    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const FUNCTION_NAME = "getClassFee.php";
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;

    axios
      .get(URL, {
        params: {
          school_code: SCHOOL_CODE,
          class_id: classId,
          session_id: sessionId,
        },
      })
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);
        if (response.api_status === "OK") {
          setClassFee(response.data);
        } else {
          myToast.notify_warning(response.api_remark);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching class fee:", error);
      });
  };

  const handleEditFee = (item) => {
    setEditFeeItem(item);
  };

  const handleCloseEditFee = () => {
    setEditFeeItem(null);
  };

  const handleEditFeeSaved = () => {
    if (CLASS_ID && SESSION_ID) {
      getClassFee(CLASS_ID, SESSION_ID);
    }
  };

  const addClassFeePath = `/AdminMain/AddClassFee/${SELECTED_CLASS_POSITION || "0"}`;

  const addClassFeeButtonStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    width: "100%",
    fontSize: "17px",
    fontWeight: 700,
    padding: "14px 20px",
    borderRadius: "10px",
    border: `2px solid ${COLORS.primary_color_shade8}`,
    background: `linear-gradient(135deg, ${COLORS.primary_color_shade5}, ${COLORS.primary_color_shade8})`,
    boxShadow: `0 4px 14px rgba(2, 119, 189, 0.35)`,
    color: COLORS.white,
    textDecoration: "none",
    cursor: "pointer",
    lineHeight: 1.3,
  };

  if (!loggedIn) return <Redirect to="/Login" />;

  const hasClassSelected = Boolean(CLASS_ID && SESSION_ID);

  return (
    <Router>
      <Route
        path="/AdminMain/ClassFee"
        exact
        render={() => (
          <div className="content" style={{ background: COLORS.grey1 }}>
            <div className="content-header d-flex align-items-center justify-content-between">
              <div className="leftside-content-header">
                <ul className="breadcrumbs">
                  <li>
                    <i className="fa fa-home" aria-hidden="true" />
                    <a href="#"> Class Fee </a>
                  </li>
                </ul>
              </div>
              <BackButton />
            </div>

            <div
              className="bg_page_white_rounded box_shadow"
              style={{ "--mMargin": "25px", padding: "0 0 48px 0" }}
            >
              <div
                style={{
                  background: CARD_GRADIENT,
                  borderRadius: "12px 12px 0 0",
                  padding: "20px 24px",
                  color: COLORS.white,
                  marginBottom: "20px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "12px",
                      background: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "22px",
                      flexShrink: 0,
                    }}
                  >
                    <i className="fa fa-rupee-sign" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontWeight: 700, fontSize: "20px" }}>
                      Class Fee Structure
                    </h4>
                    <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: "13px" }}>
                      View and manage applicable fees, receipt modes, and months for each class
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ padding: "0 20px" }}>
                <SelectClass action={CallBackClassSelect} selected_position="0" />

                {hasClassSelected && !loading && sortedClassFee.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      margin: "12px 0 14px",
                    }}
                  >
                    <div
                      style={{
                        flex: "1 1 120px",
                        background: `linear-gradient(135deg, ${COLORS.green_light}, ${COLORS.white})`,
                        border: `1px solid ${COLORS.primary_color_shade1}`,
                        borderRadius: "8px",
                        padding: "6px 12px",
                      }}
                    >
                      <div style={{ fontSize: "10px", color: COLORS.black6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.2 }}>
                        Fee Types
                      </div>
                      <div style={{ fontSize: "18px", fontWeight: 700, color: COLORS.black8, marginTop: "2px", lineHeight: 1.1 }}>
                        {sortedClassFee.length}
                      </div>
                    </div>
                    <div
                      style={{
                        flex: "1 1 140px",
                        background: `linear-gradient(135deg, ${COLORS.primary_color_shade0}, ${COLORS.white})`,
                        border: `1px solid ${COLORS.primary_color_shade1}`,
                        borderRadius: "8px",
                        padding: "6px 12px",
                      }}
                    >
                      <div style={{ fontSize: "10px", color: COLORS.black6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.2 }}>
                        Total
                      </div>
                      <div style={{ fontSize: "18px", fontWeight: 700, color: COLORS.primary_color_shade8, marginTop: "2px", lineHeight: 1.1 }}>
                        {formatINR(totalFeeValue)}
                      </div>
                    </div>
                  </div>
                )}

                <section style={{ marginTop: "8px" }}>
                  {loading ? (
                    <div style={{ padding: "40px 0" }}>
                      <MyLoader />
                    </div>
                  ) : !hasClassSelected ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "48px 24px",
                        borderRadius: "12px",
                        border: `1px dashed ${COLORS.grey4}`,
                        background: COLORS.grey0,
                        color: COLORS.black6,
                      }}
                    >
                      <i className="fa fa-graduation-cap" style={{ fontSize: "36px", color: COLORS.primary_color_shade5, marginBottom: "12px" }} />
                      <p style={{ margin: 0, fontSize: "15px", fontWeight: 600, color: COLORS.black7 }}>
                        Select a class to view fee structure
                      </p>
                      <p style={{ margin: "6px 0 0", fontSize: "13px" }}>
                        Choose session, class, and section above to load applicable fees
                      </p>
                    </div>
                  ) : sortedClassFee.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "48px 24px",
                        borderRadius: "12px",
                        border: `1px dashed ${COLORS.grey4}`,
                        background: COLORS.grey0,
                        color: COLORS.black6,
                      }}
                    >
                      <i className="fa fa-inbox" style={{ fontSize: "36px", color: COLORS.grey5, marginBottom: "12px" }} />
                      <p style={{ margin: 0, fontSize: "15px", fontWeight: 600, color: COLORS.black7 }}>
                        No fees configured for this class
                      </p>
                      <p style={{ margin: "6px 0 0", fontSize: "13px" }}>
                        Use the button below to add class fees
                      </p>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                        gap: "16px",
                      }}
                    >
                      {sortedClassFee.map((item, index) => {
                        const monthRows = getFeeMonthRows(item);
                        const readmMonthRows = getReadmMonthRows(item);
                        const isSpecialFee = String(item.is_special_fee) === "1";
                        const feeMonthTotal = sumMonthValues(monthRows) || Number(item.fee_value || 0);

                        return (
                          <div
                            key={`${item.fee_id || item.fee_name}-${index}`}
                            style={{
                              background: COLORS.white,
                              borderRadius: "12px",
                              border: `1px solid ${COLORS.grey3}`,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                              overflow: "hidden",
                              display: "flex",
                              flexDirection: "column",
                              transition: "transform 0.2s ease, box-shadow 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-2px)";
                              e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)";
                            }}
                          >
                            <div
                              style={{
                                background: CARD_GRADIENT,
                                padding: "14px 16px",
                                color: COLORS.white,
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: "12px",
                              }}
                            >
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    marginBottom: "8px",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "32px",
                                      height: "32px",
                                      borderRadius: "8px",
                                      background: "rgba(255,255,255,0.2)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontWeight: 700,
                                      fontSize: "14px",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {(item.fee_name || "F").charAt(0).toUpperCase()}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "16px",
                                      fontWeight: 700,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {item.fee_name || "Unknown Fee"}
                                  </div>
                                </div>
                                <span
                                  style={{
                                    display: "inline-block",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    padding: "3px 10px",
                                    borderRadius: "999px",
                                    background: "rgba(255,255,255,0.2)",
                                    border: "1px solid rgba(255,255,255,0.25)",
                                  }}
                                >
                                  {item.mode || "—"}
                                </span>
                              </div>
                              <div
                                style={{
                                  flexShrink: 0,
                                  textAlign: "right",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "20px",
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {formatINR(item.fee_value)}
                                </div>
                                <div
                                  style={{
                                    fontSize: "11px",
                                    opacity: 0.9,
                                    marginTop: "4px",
                                    fontWeight: 600,
                                  }}
                                >
                                  {monthRows.length > 1
                                    ? `Total ${formatINR(feeMonthTotal)}`
                                    : "Per month"}
                                </div>
                              </div>
                            </div>

                            <div style={{ padding: "14px 16px", flex: 1 }}>
                              <div
                                style={{
                                  fontSize: "12px",
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.08em",
                                  color: COLORS.black8,
                                  marginBottom: "12px",
                                }}
                              >
                                Fee by Month
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(4, 1fr)",
                                  gap: "8px",
                                }}
                              >
                                {monthRows.map((monthRow, monthIndex) => (
                                  <MonthFeeChip
                                    key={`${monthRow.month}-${monthIndex}`}
                                    monthKey={monthRow.month}
                                    feeValue={monthRow.fee_value}
                                  />
                                ))}
                              </div>

                              {isSpecialFee && readmMonthRows.length > 0 && (
                                <>
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: 800,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.08em",
                                      color: COLORS.black8,
                                      margin: "16px 0 12px",
                                    }}
                                  >
                                    Readmission Fee by Month
                                  </div>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "repeat(4, 1fr)",
                                      gap: "8px",
                                    }}
                                  >
                                    {readmMonthRows.map((monthRow, monthIndex) => (
                                      <MonthFeeChip
                                        key={`readm-${monthRow.month}-${monthIndex}`}
                                        monthKey={monthRow.month}
                                        feeValue={monthRow.fee_value}
                                        accentColor={COLORS.card_green}
                                      />
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>

                            <div style={{ padding: "0 16px 14px" }}>
                              <button
                                type="button"
                                className="btn btn-info"
                                onClick={() => handleEditFee(item)}
                                style={{
                                  width: "100%",
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  padding: "8px 12px",
                                  borderRadius: "8px",
                                  border: `1px solid ${COLORS.primary_color_shade3}`,
                                  background: COLORS.primary_color_shade0,
                                  color: COLORS.primary_color_shade8,
                                }}
                              >
                                <i className="fa fa-edit" /> Edit
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>

                <div style={{ marginTop: "28px", paddingBottom: "8px" }}>
                  <Link to={addClassFeePath} style={addClassFeeButtonStyle}>
                    <i className="fa fa-plus-circle" aria-hidden="true" />
                    Add Class Fee
                  </Link>
                </div>
              </div>
            </div>

            {editFeeItem && (
              <MdlEditClassFee
                feeItem={editFeeItem}
                sessionId={SESSION_ID}
                classId={CLASS_ID}
                sectionId={SECTION_ID}
                actionClose={handleCloseEditFee}
                onSaved={handleEditFeeSaved}
              />
            )}
          </div>
        )}
      />

      <Route
        path="/AdminMain/AddClassFee/:selected_class_pos"
        component={AddClassFee}
      />
    </Router>
  );
};

export default ClassFee;
